import { forwardRef } from "react";

interface PreviewSource {
  src: string;
  type: string;
  label: "raw" | "mp3";
}

interface PreviewPlayerProps {
  sources: PreviewSource[];
  debugMode?: boolean;
}

/**
 * Preview audio player component for RecorderPanel.
 * Shows HTML5 audio player with multiple source fallbacks.
 */
export const PreviewPlayer = forwardRef<HTMLAudioElement, PreviewPlayerProps>(
  function PreviewPlayer({ sources, debugMode = false }, ref) {
    if (!sources.length) {
      return null;
    }

    return (
      <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
        <audio
          ref={ref}
          key={sources[0]?.src}
          className="w-full"
          controls
          preload="auto"
          onError={(e) => {
            console.error("Audio playback error:", e);
            const audio = e.currentTarget;
            console.error("Audio error details:", {
              error: audio.error,
              networkState: audio.networkState,
              readyState: audio.readyState,
              src: audio.src,
              sources,
            });
          }}
          onLoadedData={(e) => {
            if (debugMode) {
              const audio = e.currentTarget;
              console.log("Audio loaded successfully", {
                duration: audio.duration,
                readyState: audio.readyState,
                networkState: audio.networkState,
                muted: audio.muted,
                volume: audio.volume,
                paused: audio.paused,
                src: audio.src,
                currentSrc: audio.currentSrc,
              });
            }
          }}
          onCanPlay={() => {
            if (debugMode) {
              console.log("Audio can play");
            }
          }}
          onPlay={(e) => {
            if (debugMode) {
              const audio = e.currentTarget;
              console.log("Audio started playing", {
                currentTime: audio.currentTime,
                duration: audio.duration,
                muted: audio.muted,
                volume: audio.volume,
              });
            }
          }}
          onTimeUpdate={(e) => {
            if (debugMode) {
              const audio = e.currentTarget;
              if (audio.currentTime > 0.1 && audio.paused === false) {
                console.log("Audio is playing, currentTime:", audio.currentTime);
              }
            }
          }}
          onPause={() => {
            if (debugMode) {
              console.log("Audio paused");
            }
          }}
        >
          {sources.map((source) => (
            <source key={source.src} src={source.src} type={source.type} />
          ))}
          Your browser does not support playback of this recording.
        </audio>
      </div>
    );
  }
);
